import {
  BaseFileUpload,
  BaseFormActions,
  BaseInput,
  BaseTextarea,
  createActions,
} from '@/components';
import { useConfirm, useUnsavedChanges } from '@/hooks';
import { trans } from '@/i18n';
import { ROUTE_PATH } from '@/routes';
import { IPlanForm, IPlanFormData } from '@/types';
import { showError, showSuccess } from '@/utils';
import { performSecurityCheck, sanitizeContent } from '@/utils/security';
import { Box, Flex } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import '../styles/create-plan.scss';
import createPlanYup from '../utils/createPlan.validate';

interface PlanFormProps {
  mode: 'create' | 'edit' | 'detail';
  initialData?: IPlanFormData;
  fileUrls?: Record<string, { url: string; fileName: string; originName?: string }>;
  onSubmit?: (data: IPlanForm, reset?: (data: IPlanFormData) => void) => Promise<void>;
  onCancel?: () => void;
  onChange?: (data: IPlanFormData) => void;
  onDelete?: () => void;
  onUpdate?: () => void;
  loading?: boolean;
  loadingDelete?: boolean;
  loadingUpdate?: boolean;
  hasUnsavedChanges?: boolean;
  isDirty?: boolean;
}

function PlanForm({
  mode,
  initialData,
  fileUrls,
  onSubmit,
  onCancel,
  onChange,
  onDelete,
  onUpdate,
  loading: externalLoading,
  loadingDelete,
  loadingUpdate,
  hasUnsavedChanges = false,
}: PlanFormProps) {
  const navigate = useNavigate();
  const [internalLoading, setInternalLoading] = useState<boolean>(false);
  const { toggleConfirm } = useConfirm();
  const { setUnsavedChanges, resetChanges } = useUnsavedChanges();

  const loading = externalLoading !== undefined ? externalLoading : internalLoading;
  const isDetail = mode === 'detail';

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
    trigger,
    watch,
  } = useForm<IPlanFormData>({
    mode: 'onBlur',
    resolver: createPlanYup,
    defaultValues: initialData,
  });

  const watchedValues = watch();

  useEffect(() => {
    if (mode === 'detail' && initialData) {
      reset(initialData);
    }
  }, [initialData, mode]);

  const isChanged = useMemo(() => {
    if (mode === 'detail') return hasUnsavedChanges;
    return Object.values(watchedValues).some((value) => {
      if (value instanceof File) {
        return value.name !== undefined;
      }
      return value !== undefined && value !== null && value?.trim() !== '';
    });
  }, [watchedValues, hasUnsavedChanges]);

  useEffect(() => {
    setUnsavedChanges(isChanged);

    return () => {
      resetChanges();
    };
  }, [isChanged, setUnsavedChanges]);

  useEffect(() => {
    if (onChange && watchedValues) {
      onChange(watchedValues);
    }
  }, [watchedValues, onChange]);

  const hasMissingFiles = (data: IPlanForm): boolean => {
    return !data.htmlFile || !data.cssFile || !data.jsFile || !data.imageZipFile;
  };

  const checkFileSecurity = async (file: File, fileType: 'html' | 'css' | 'js'): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        const violations = performSecurityCheck(content, fileType);

        if (violations.length > 0) {
          const sanitizedContent = sanitizeContent(content, fileType);
          const sanitizedFile = new File([sanitizedContent], file.name, {
            type: file.type,
            lastModified: file.lastModified,
          });

          resolve(sanitizedFile);
        } else {
          resolve(file);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  const getFileType = (fileName: string): 'html' | 'css' | 'js' | null => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'html':
      case 'htm':
        return 'html';
      case 'css':
        return 'css';
      case 'js':
        return 'js';
      default:
        return null;
    }
  };

  const handleSubmitWithConfirmation = async (data: IPlanFormData) => {
    const bodyPlan: IPlanForm = {
      name: data?.name?.trim(),
      price: data?.price || 0,
      description: data?.description?.trim(),
      htmlFile: data?.htmlFile,
      cssFile: data?.cssFile,
      jsFile: data?.jsFile,
      imageZipFile: data?.imageZipFile,
    };

    if (mode === 'create' && hasMissingFiles(bodyPlan)) {
      toggleConfirm({
        title: trans('plan.missingFilesTitle'),
        message: trans('plan.missingFilesMessage'),
        okText: trans('plan.yes'),
        cancelText: trans('plan.no'),
        onOk: () => {
          submitPlan(bodyPlan);
        },
      });
      return;
    }

    await submitPlan(bodyPlan);
  };

  const submitPlan = async (bodyPlan: IPlanForm) => {
    try {
      setInternalLoading(true);

      const sanitizedPlan: IPlanForm = { ...bodyPlan };

      if (bodyPlan.htmlFile) {
        const fileType = getFileType(bodyPlan.htmlFile.name);
        if (fileType) {
          sanitizedPlan.htmlFile = await checkFileSecurity(bodyPlan.htmlFile, fileType);
        }
      }

      if (bodyPlan.cssFile) {
        const fileType = getFileType(bodyPlan.cssFile.name);
        if (fileType) {
          sanitizedPlan.cssFile = await checkFileSecurity(bodyPlan.cssFile, fileType);
        }
      }

      if (bodyPlan.jsFile) {
        const fileType = getFileType(bodyPlan.jsFile.name);
        if (fileType) {
          sanitizedPlan.jsFile = await checkFileSecurity(bodyPlan.jsFile, fileType);
        }
      }

      if (onSubmit) {
        await onSubmit(sanitizedPlan, reset);
      } else {
        if (mode === 'create') {
          showSuccess(trans('plan.createSuccess'));
          navigate(ROUTE_PATH.PLAN);
        }
      }
    } catch (error) {
      showError(error);
      const formData: IPlanFormData = {
        name: bodyPlan.name,
        price: bodyPlan.price,
        description: bodyPlan.description,
        htmlFile: bodyPlan.htmlFile,
        cssFile: bodyPlan.cssFile,
        jsFile: bodyPlan.jsFile,
        imageZipFile: bodyPlan.imageZipFile,
      };
      reset(formData);
    } finally {
      setInternalLoading(false);
    }
  };

  const handleFormSubmit: SubmitHandler<IPlanFormData> = async (data) => {
    await handleSubmitWithConfirmation(data);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate(ROUTE_PATH.PLAN);
    }
  };

  const isReadOnly = false;

  const getFileName = useCallback((fileName: string | undefined) => {
    if (!fileName) return '';
    return fileName.split('/').pop();
  }, []);

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Box className='plan-detail-page-form-box'>
        <Box mb={isDetail ? '29px' : '19px'}>
          <Flex gap={isDetail ? '45px' : '26px'} className='plan-detail-page-form-input'>
            <BaseInput
              errors={errors}
              control={control}
              name="name"
              label={trans('plan.name')}
              width="430px"
              span={1}
              placeholder="例）フェイシャル"
              isRequired
              disabled={isReadOnly}
            />
            <BaseInput
              errors={errors}
              control={control}
              name="price"
              label={trans('plan.price')}
              width="430px"
              span={1}
              placeholder="例) 100,000"
              isRequired
              isCurrency
              disabled={isReadOnly}
            />
          </Flex>
        </Box>

        <Box mb={isDetail ? '23px' : '18px'} className='plan-detail-page-form-textarea'>
          <BaseTextarea
            errors={errors}
            control={control}
            name="description"
            label={trans('plan.description')}
            width="430px"
            span={1}
            placeholder="プランの詳細を記入..."
            disabled={isReadOnly}
          />
        </Box>

        <Box>
          <Flex gap={'18px'} className='plan-detail-page-form-file-upload'>
            <BaseFileUpload
              className="base-file-upload"
              errors={errors}
              control={control}
              name="htmlFile"
              label={trans('plan.htmlFile')}
              placeholder={trans('plan.htmlFilePlaceholder')}
              accept={['.html']}
              setValue={setValue}
              trigger={trigger}
              span={1}
              fileType="html"
              disabled={isReadOnly}
              fileUrl={fileUrls?.htmlFile?.url}
              fileName={getFileName(fileUrls?.htmlFile?.fileName)}
            />
            <BaseFileUpload
              className="base-file-upload"
              errors={errors}
              control={control}
              name="cssFile"
              label={trans('plan.cssFile')}
              placeholder={trans('plan.cssFilePlaceholder')}
              accept={['.css']}
              setValue={setValue}
              trigger={trigger}
              span={1}
              fileType="css"
              disabled={isReadOnly}
              fileUrl={fileUrls?.cssFile?.url}
              fileName={getFileName(fileUrls?.cssFile?.fileName)}
            />
            <BaseFileUpload
              className="base-file-upload"
              errors={errors}
              control={control}
              name="jsFile"
              label={trans('plan.jsFile')}
              placeholder={trans('plan.jsFilePlaceholder')}
              accept={['.js']}
              setValue={setValue}
              trigger={trigger}
              span={1}
              fileType="js"
              disabled={isReadOnly}
              fileUrl={fileUrls?.jsFile?.url}
              fileName={getFileName(fileUrls?.jsFile?.fileName)}
            />
            <BaseFileUpload
              className="base-file-upload"
              errors={errors}
              control={control}
              name="imageZipFile"
              label={trans('plan.imageZipFile')}
              placeholder={trans('plan.imageZipFilePlaceholder')}
              accept={['.zip']}
              setValue={setValue}
              trigger={trigger}
              span={1}
              fileType="zip"
              disabled={isReadOnly}
              fileUrl={fileUrls?.imageZipFile?.url}
              fileName={getFileName(fileUrls?.imageZipFile?.fileName)}
            />
          </Flex>
        </Box>

        {/* Form actions */}
        <BaseFormActions
          actions={
            mode === 'detail'
              ? createActions.detail(
                onDelete,
                onUpdate,
                loadingDelete,
                loadingUpdate,
                hasUnsavedChanges
              )
              : createActions.createEdit(
                () => handleSubmit(handleFormSubmit)(),
                handleCancel,
                loading,
                trans('plan.btnCreate')
              )
          }
          marginTop={mode === 'detail' ? '42px' : '60px'}
        />
      </Box>
    </form>
  );
}

export default PlanForm;
